import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "./fs";
import { db } from "../config/firebase";
import { IDLE_TIMEOUT_MS } from "./sessionTimeout";
import { SESSION_LIMIT } from "./sessions";

/**
 * Per-tenant session policy (`sessionPolicies/{tenantId}`, FR-AUT-11), shared with mobile.
 * Build-time env values are the defaults until a manager saves a policy. The rules enforce
 * the same bounds; a tenant manager cannot go above the licensed seat count.
 */
export const IDLE_MINUTES_RANGE = [5, 480];
export const PLATFORM_MAX_SESSION_LIMIT = 1000;
export const LICENSED_SESSION_LIMIT = 20;

export const DEFAULT_POLICY = Object.freeze({
  idleMinutes: Math.round(IDLE_TIMEOUT_MS / 60000),
  sessionLimit: SESSION_LIMIT
});

const intIn = (value, [min, max]) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

export function maxSessionLimit(isPlatformAdmin) {
  return isPlatformAdmin ? PLATFORM_MAX_SESSION_LIMIT : LICENSED_SESSION_LIMIT;
}

/** Stored values when valid, otherwise the defaults (a bad document never locks users out). */
export function normalizePolicy(data) {
  return {
    idleMinutes: intIn(data?.idleMinutes, IDLE_MINUTES_RANGE) ?? DEFAULT_POLICY.idleMinutes,
    sessionLimit: intIn(data?.sessionLimit, [1, PLATFORM_MAX_SESSION_LIMIT]) ?? DEFAULT_POLICY.sessionLimit
  };
}

/** Field errors for the admin form: { idleMinutes?: [min, max], sessionLimit?: [min, max] }. */
export function validatePolicy(policy, { isPlatformAdmin = false } = {}) {
  const errors = {};
  if (intIn(policy.idleMinutes, IDLE_MINUTES_RANGE) == null) errors.idleMinutes = IDLE_MINUTES_RANGE;
  const limitRange = [1, maxSessionLimit(isPlatformAdmin)];
  if (intIn(policy.sessionLimit, limitRange) == null) errors.sessionLimit = limitRange;
  return errors;
}

const policyRef = (tenantId) => doc(db, "sessionPolicies", tenantId);

export async function getSessionPolicy(tenantId) {
  if (!tenantId) return { ...DEFAULT_POLICY };
  try {
    const snap = await getDoc(policyRef(tenantId));
    return normalizePolicy(snap.exists() ? snap.data() : null);
  } catch {
    return { ...DEFAULT_POLICY };
  }
}

export function watchSessionPolicy(tenantId, onChange) {
  if (!tenantId) {
    onChange({ ...DEFAULT_POLICY });
    return () => {};
  }
  return onSnapshot(
    policyRef(tenantId),
    (snap) => onChange(normalizePolicy(snap.exists() ? snap.data() : null)),
    () => onChange({ ...DEFAULT_POLICY })
  );
}

export async function saveSessionPolicy(tenantId, policy, uid) {
  await setDoc(policyRef(tenantId), {
    idleMinutes: Number(policy.idleMinutes),
    sessionLimit: Number(policy.sessionLimit),
    updatedAt: serverTimestamp(),
    updatedBy: uid
  });
}
